/**
 * useChessSounds — satisfying chess sounds via Lichess's free audio assets.
 *
 * Sounds are fetched from Lichess's CDN on first use and cached in memory.
 * No npm package needed — pure Web Audio API.
 *
 * USAGE in RoomPage:
 *   const { playMove, playCapture, playCheck, playCheckmate, playJoin } = useChessSounds()
 *
 *   // After a move is made:
 *   if (isCapture) playCapture()
 *   else if (isCheckmate) playCheckmate()
 *   else if (isCheck) playCheck()
 *   else playMove()
 *
 *   // When guest joins:
 *   playJoin()
 */
import { useRef, useCallback } from 'react'

const SOUNDS = {
  move:      'https://lichess1.org/assets/sound/standard/Move.mp3',
  capture:   'https://lichess1.org/assets/sound/standard/Capture.mp3',
  check:     'https://lichess1.org/assets/sound/standard/GenericNotify.mp3',
  checkmate: 'https://lichess1.org/assets/sound/standard/Victory.mp3',
  join:      'https://lichess1.org/assets/sound/standard/GenericNotify.mp3',
}

export function useChessSounds(volume = 0.6) {
  const cache = useRef({})
  const ctx = useRef(null)

  function getCtx() {
    if (!ctx.current) ctx.current = new (window.AudioContext || window.webkitAudioContext)()
    return ctx.current
  }

  async function load(key) {
    if (cache.current[key]) return cache.current[key]
    try {
      const res = await fetch(SOUNDS[key])
      const arrayBuffer = await res.arrayBuffer()
      const audioBuffer = await getCtx().decodeAudioData(arrayBuffer)
      cache.current[key] = audioBuffer
      return audioBuffer
    } catch (err) {
      console.warn('[useChessSounds] Failed to load sound:', key, err)
      return null
    }
  }

  async function play(key) {
    const buffer = await load(key)
    if (!buffer) return
    try {
      const ac = getCtx()
      // Resume context if suspended (browser autoplay policy)
      if (ac.state === 'suspended') await ac.resume()
      const source = ac.createBufferSource()
      source.buffer = buffer
      const gainNode = ac.createGain()
      gainNode.gain.value = volume
      source.connect(gainNode)
      gainNode.connect(ac.destination)
      source.start(0)
    } catch (err) {
      console.warn('[useChessSounds] Failed to play sound:', key, err)
    }
  }

  // Preload all sounds on first user interaction (called from RoomPage on mount)
  const preload = useCallback(() => {
    Object.keys(SOUNDS).forEach(load)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const playMove      = useCallback(() => play('move'),      [volume]) // eslint-disable-line react-hooks/exhaustive-deps
  const playCapture   = useCallback(() => play('capture'),   [volume]) // eslint-disable-line react-hooks/exhaustive-deps
  const playCheck     = useCallback(() => play('check'),     [volume]) // eslint-disable-line react-hooks/exhaustive-deps
  const playCheckmate = useCallback(() => play('checkmate'), [volume]) // eslint-disable-line react-hooks/exhaustive-deps
  const playJoin      = useCallback(() => play('join'),      [volume]) // eslint-disable-line react-hooks/exhaustive-deps

  return { preload, playMove, playCapture, playCheck, playCheckmate, playJoin }
}
